import React, { useState } from 'react';
import { MoreVertical, Edit2, Copy, Download, Trash2, Check, X, Disc } from 'lucide-react';
import { Wheel } from '../../types';

interface WheelItemProps {
  wheel: Wheel;
  isActive: boolean;
  onSelect: (id: string) => void;
  onRename: (id: string, newName: string) => void;
  onDuplicate: (id: string) => void;
  onExport: (wheel: Wheel) => void;
  onDelete: (id: string) => void;
  isOnlyWheel: boolean;
}

export const WheelItem: React.FC<WheelItemProps> = ({
  wheel,
  isActive,
  onSelect,
  onRename,
  onDuplicate,
  onExport,
  onDelete,
  isOnlyWheel,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [nameVal, setNameVal] = useState(wheel.name);
  const [showMenu, setShowMenu] = useState(false);

  const handleSaveRename = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (nameVal.trim() && nameVal.trim() !== wheel.name) {
      onRename(wheel.id, nameVal.trim());
    }
    setIsEditing(false);
  };

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.65rem 0.85rem',
        borderRadius: '12px',
        background: isActive ? 'var(--bg-surface-elevated)' : 'transparent',
        border: isActive ? '1px solid var(--border-focus)' : '1px solid transparent',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        boxShadow: isActive ? '0 4px 12px rgba(99, 102, 241, 0.15)' : 'none',
      }}
      onClick={() => {
        if (!isEditing) onSelect(wheel.id);
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
        <Disc
          size={18}
          color={isActive ? 'var(--accent-primary)' : 'var(--text-muted)'}
          style={{ flexShrink: 0 }}
        />

        {isEditing ? (
          <form
            onSubmit={handleSaveRename}
            onClick={(e) => e.stopPropagation()}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1 }}
          >
            <input
              type="text"
              value={nameVal}
              onChange={(e) => setNameVal(e.target.value)}
              autoFocus
              style={{
                padding: '2px 6px',
                fontSize: '0.85rem',
                height: '28px',
              }}
            />
            <button type="submit" className="btn-ghost" style={{ padding: '4px', color: '#10b981' }}>
              <Check size={14} />
            </button>
            <button
              type="button"
              onClick={() => {
                setNameVal(wheel.name);
                setIsEditing(false);
              }}
              className="btn-ghost"
              style={{ padding: '4px', color: '#ef4444' }}
            >
              <X size={14} />
            </button>
          </form>
        ) : (
          <span
            style={{
              fontSize: '0.9rem',
              fontWeight: isActive ? 600 : 500,
              color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {wheel.name}
          </span>
        )}
      </div>

      {!isEditing && (
        <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="btn-ghost"
            style={{ padding: '4px', color: 'var(--text-muted)' }}
            title="Wheel options"
          >
            <MoreVertical size={16} />
          </button>

          {showMenu && (
            <div
              className="glass-panel"
              style={{
                position: 'absolute',
                right: '0',
                top: '28px',
                zIndex: 50,
                width: '160px',
                padding: '4px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-subtle)',
                borderRadius: '10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }}
            >
              <button
                onClick={() => {
                  setShowMenu(false);
                  setIsEditing(true);
                }}
                className="btn-ghost"
                style={{ width: '100%', justifyContent: 'flex-start', fontSize: '0.8rem', padding: '6px 8px' }}
              >
                <Edit2 size={13} />
                Rename
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  onDuplicate(wheel.id);
                }}
                className="btn-ghost"
                style={{ width: '100%', justifyContent: 'flex-start', fontSize: '0.8rem', padding: '6px 8px' }}
              >
                <Copy size={13} />
                Duplicate
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  onExport(wheel);
                }}
                className="btn-ghost"
                style={{ width: '100%', justifyContent: 'flex-start', fontSize: '0.8rem', padding: '6px 8px' }}
              >
                <Download size={13} />
                Export JSON
              </button>

              {!isOnlyWheel && (
                <button
                  onClick={() => {
                    setShowMenu(false);
                    if (window.confirm(`Delete "${wheel.name}"? This cannot be undone.`)) {
                      onDelete(wheel.id);
                    }
                  }}
                  className="btn-ghost"
                  style={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    fontSize: '0.8rem',
                    padding: '6px 8px',
                    color: '#ef4444',
                  }}
                >
                  <Trash2 size={13} />
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
